import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, DollarSign, Target, BarChart3, Award } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#1A7AB8', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

function formatCurrency(v) { return '$' + Math.abs(v || 0).toFixed(0); }

function KpiCard({ icon, iconBg, label, value, color, testId }) {
  const Icon = icon;
  return (
    <Card><CardContent className="p-5 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center`}><Icon className={`w-6 h-6 ${color}`} /></div>
      <div><p className="text-sm text-slate-500">{label}</p><p className={`text-2xl font-bold ${color || ''}`} data-testid={testId}>{value}</p></div>
    </CardContent></Card>
  );
}

function TopSavingsTable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="border-b text-slate-500"><th className="py-2 text-left">Customer</th><th className="py-2 text-right">Savings</th><th className="py-2 text-right">%</th></tr></thead>
        <tbody>
          {rows.map(function(row, i) {
            return (
              <tr key={i} className="border-b last:border-0">
                <td className="py-2 font-medium">{row.customer}</td>
                <td className="py-2 text-right text-green-600 font-semibold">{formatCurrency(row.savings)}</td>
                <td className="py-2 text-right">{row.percentage.toFixed(1)}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function Analytics() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(function() {
    axios.get(API_URL + '/api/analytics?authorization=' + token)
      .then(function(r) { setData(r.data); })
      .catch(function() {})
      .finally(function() { setLoading(false); });
  }, [token]);

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" /></div></Layout>;
  if (!data) return <Layout><p>Failed to load analytics</p></Layout>;

  var pieData = [];
  var notSent = data.total_comparisons - data.quote_stats.sent - data.quote_stats.accepted;
  if (notSent > 0) pieData.push({ name: 'Not Sent', value: notSent });
  if (data.quote_stats.sent > 0) pieData.push({ name: 'Sent', value: data.quote_stats.sent });
  if (data.quote_stats.accepted > 0) pieData.push({ name: 'Accepted', value: data.quote_stats.accepted });

  return (
    <Layout>
      <div className="space-y-6" data-testid="analytics-page">
        <h1 className="text-2xl font-bold text-slate-900">Analytics</h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard icon={BarChart3} iconBg="bg-blue-50" label="Total Comparisons" value={data.total_comparisons} color="text-blue-600" testId="stat-total" />
          <KpiCard icon={DollarSign} iconBg="bg-green-50" label="Total Savings" value={formatCurrency(data.total_savings)} color="text-green-600" testId="stat-savings" />
          <KpiCard icon={TrendingUp} iconBg="bg-amber-50" label="Avg Savings" value={formatCurrency(data.avg_savings)} color="text-amber-600" testId="stat-avg" />
          <KpiCard icon={Target} iconBg="bg-purple-50" label="Win Rate" value={data.win_rate.toFixed(1) + '%'} color="text-purple-600" testId="stat-winrate" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader><CardTitle className="text-base">Monthly Comparisons & Savings</CardTitle></CardHeader>
            <CardContent>
              {data.monthly_trend.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={data.monthly_trend}>
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#1A7AB8" radius={[4, 4, 0, 0]} name="Comparisons" />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-center text-slate-400 py-12">No data yet</p>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Quote Status</CardTitle></CardHeader>
            <CardContent>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" cx="50%" cy="50%" outerRadius={100} label>
                      {pieData.map(function(entry, i) { return <Cell key={i} fill={COLORS[i % COLORS.length]} />; })}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : <div className="flex flex-col items-center justify-center py-12 text-slate-400"><Target className="w-12 h-12 mb-3" /><p>No quotes sent yet</p></div>}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Award className="w-5 h-5 text-amber-500" />Top Savings</CardTitle></CardHeader>
          <CardContent>
            {data.top_savings.length > 0 ? <TopSavingsTable rows={data.top_savings} /> : <p className="text-center text-slate-400 py-8">No savings data yet</p>}
          </CardContent>
        </Card>

        {data.by_tariff.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-base">Performance by Tariff</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={Math.max(200, data.by_tariff.length * 40)}>
                <BarChart data={data.by_tariff} layout="vertical">
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={200} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="total_savings" fill="#1A7AB8" radius={[0, 4, 4, 0]} name="Total Savings" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
